import { clientDiagnostics } from '~/api';

export type PreAuthStage = 'recaptcha' | 'request';

export type PreAuthAction = 'login' | 'reset-password' | 'first-access';

type PreAuthContext = {
  stage: PreAuthStage;
  action: PreAuthAction;
  realm: string;
};

const reported = new WeakSet<object>();

const isObject = (thrown: unknown): thrown is object =>
  typeof thrown === 'object' && thrown !== null;

const reachedTheBackend = (thrown: unknown): boolean =>
  isObject(thrown) && 'response' in thrown;

const alreadyReported = (thrown: unknown): boolean =>
  isObject(thrown) && reported.has(thrown);

const markAsReported = (thrown: unknown): void => {
  if (isObject(thrown)) reported.add(thrown);
};

const toMessage = (thrown: unknown): string =>
  thrown instanceof Error ? thrown.message : String(thrown);

/**
 * Reports a login, password reset or first access failure that happened before
 * any request left the browser, which is the one case auth-management cannot
 * log on its side. Failures carrying a response are skipped: the service
 * already records those.
 *
 * Sends no email and no credentials — nobody is authenticated at this point, so
 * the realm is the only identifier worth correlating on. The screen keeps
 * showing the generic message; this only feeds the service log.
 *
 * A rethrown failure passes through more than one call site, so each error is
 * reported once, keeping the stage closest to where it originated.
 */
export function reportPreAuthError(
  thrown: unknown,
  { stage, action, realm }: PreAuthContext
): void {
  if (reachedTheBackend(thrown) || alreadyReported(thrown)) return;

  markAsReported(thrown);

  try {
    clientDiagnostics
      .report({ realm, stage, action, message: toMessage(thrown) })
      .catch(() => undefined);
  } catch {
    // Observability must never break the authentication flow.
  }
}
